<template>
  <div class="product-family-head-fi">
    <grid-container-fi :container="false">
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-end-7">
        <catalog-slider-fi
          :catalog-data="catalogData"
          :item-name="familyName"
        />
      </div>
      <div class="fi-col-start-1 fi-col-end-13 md:fi-col-start-7 md:fi-col-end-12">
        <!-- eslint-disable vue/no-v-html -->
        <headline-fi
          v-if="marketingClaim"
          class="product-family-head-fi__marketing-claim fi-mb-12"
          tag="div"
          color="brand"
          :size="30"
        >
          <span v-html="marketingClaim" />
        </headline-fi>
        <headline-fi
          v-if="familyName"
          class="product-family-head-fi__name"
          tag="h1"
          :size="60"
        >
          <span v-html="familyName" />
        </headline-fi>
        <div
          v-if="description"
          class="product-family-head-fi__description richtext-fi"
          v-html="description"
        />
        <!-- eslint-enable vue/no-v-html -->

        <link-fi
          class="product-family-head-fi__link"
          href="#"
          :size="18"
          data-test="product-variants-link"
          @click.stop.prevent="$emit('scrollToVariants')"
        >
          {{ $dict.get('product-Family.Anchor-Link-Text') }}
        </link-fi>

        <link-fi
          v-if="mediaDownloadLink && fields?.mediaDownloadLabel?.value"
          class="product-family-head-fi__link"
          rel="nofollow"
          href="#"
          :size="18"
          @click.stop.prevent="openMediaDownloadDialog"
        >
          {{ fields.mediaDownloadLabel.value }}
        </link-fi>

        <system-partner-hint-fi
          v-if="showHint"
          class="product-family-head-fi__system-partner-hint fi-mt-24"
        />
      </div>

      <media-download-dialog-fi
        ref="mediaDownloadDialog"
        :media-download-link="mediaDownloadLink"
        :file-name="familyName"
        :fields="fields"
      />
    </grid-container-fi>
  </div>
</template>

<script setup lang="ts">
import { HeadlineFi, LinkFi } from 'atoms';
import { computed, useTemplateRef } from 'vue';

import CatalogSliderFi from '@/components/catalog-slider-fi/CatalogSliderFi.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import MediaDownloadDialogFi from '@/components/product-detail/media-download-dialog-fi/MediaDownloadDialogFi.vue';
import SystemPartnerHintFi from '@/components/system-partner-hint-fi/SystemPartnerHintFi.vue';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { type SystemItemCarrier, useSystemPartner } from '@/composables/systemPartner';
import type { ProductFamilyPageFiFields } from '@/generated/fields';

defineOptions({ name: 'ProductFamilyHeadFi' });

const props = withDefaults(
  defineProps<{
    fields?: ProductFamilyPageFiFields | undefined;
    catalogData?: object | null;
    mediaDownloadLink?: string | null;
  }>(),
  {
    fields: undefined,
    catalogData: null,
    mediaDownloadLink: null,
  },
);

defineEmits<{
  scrollToVariants: [];
}>();

const mediaDownloadDialog = useTemplateRef('mediaDownloadDialog');

const { textListValue } = useCatalogTextList();
const { showSystemPartnerHint } = useSystemPartner();

const marketingClaim = computed(
  () =>
    textListValue((props.catalogData as any)?.TextList, 'KAT_POS') ||
    textListValue((props.catalogData as any)?.TextList, 'DOKBSTMARKCLAIM'),
);

const familyName = computed(
  () =>
    textListValue((props.catalogData as any)?.TextList, 'BEZEICHNUNG') ||
    textListValue((props.catalogData as any)?.TextList, 'ARTIKELLANG'),
);

const description = computed(() => (props.catalogData as any)?.description);

const showHint = computed(() =>
  showSystemPartnerHint(props.catalogData as SystemItemCarrier | null),
);

const openMediaDownloadDialog = () => {
  (mediaDownloadDialog.value as any).show();
};
</script>

<style scoped src="./product-family-head-fi" />
